import { useEffect, useMemo, useState } from 'react'
import styles from './Sparkles.module.css'

interface SparklesProps {
  /** Number of sparkle particles */
  count?: number
  /** Duration of the animation in seconds */
  duration?: number
  /** Color of the sparkles */
  color?: string
  /** Whether the animation is active */
  active?: boolean
  /** Callback when animation completes */
  onComplete?: () => void
}

interface Sparkle {
  id: number
  top: number
  left: number
  delay: number
  duration: number
  size: number
}

export function Sparkles({
  count = 30,
  duration = 4,
  color = '#f0488b',
  active = true,
  onComplete,
}: SparklesProps) {
  const [isVisible, setIsVisible] = useState(active)

  const sparkles = useMemo<Sparkle[]>(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      delay: Math.random() * 0.8,
      duration: duration * 0.6 + Math.random() * duration * 0.8,
      size: 4 + Math.random() * 12,
    }))
  }, [count, duration])

  useEffect(() => {
    setIsVisible(active)
    if (active && onComplete) {
      const timer = setTimeout(() => {
        onComplete()
      }, (duration + 1) * 1000)
      return () => clearTimeout(timer)
    }
  }, [active, duration, onComplete])

  if (!isVisible) return null

  return (
    <div className={styles.Sparkles} aria-hidden="true">
      {sparkles.map((sparkle) => (
        <div
          key={sparkle.id}
          className={styles.sparkle}
          style={
            {
              '--sparkle-top': `${sparkle.top}%`,
              '--sparkle-left': `${sparkle.left}%`,
              '--sparkle-delay': `${sparkle.delay}s`,
              '--sparkle-duration': `${sparkle.duration}s`,
              '--sparkle-size': `${sparkle.size}px`,
              '--sparkle-color': color,
            } as React.CSSProperties
          }
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            className={styles.sparkleSvg}
          >
            <path
              d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z"
              fill="currentColor"
            />
          </svg>
        </div>
      ))}
    </div>
  )
}
